import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Key, Copy, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { apiResetPassword } from '../../utils/api';
import { getAuthToken } from '../../utils/auth';

export const ResetPasswordModal = ({ user, onClose, onPasswordGenerated }) => {
    const { darkMode } = useTheme();
    const [newPassword, setNewPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const handleReset = async () => {
        setIsLoading(true);
        const token = getAuthToken();
        const data = await apiResetPassword(token, user.id);
        setIsLoading(false);
        if (data.success) {
            setNewPassword(data.password);
            onPasswordGenerated(data.password);
        } else {
            alert(`Ошибка: ${data.error}`);
            onClose();
        }
    };

    const copyToClipboard = () => {
        navigator.clipboard.writeText(newPassword)
            .then(() => alert('Пароль скопирован!'))
            .catch(() => alert('Не удалось скопировать'));
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]"
            onClick={onClose}
        >
            <div
                className={`rounded-xl shadow-2xl w-full max-w-md p-6 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}
                onClick={e => e.stopPropagation()}
            >
                <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center space-x-3">
                        <div className={`${darkMode ? 'bg-blue-900' : 'bg-blue-100'} p-2 rounded-lg`}>
                            <Key className={`w-6 h-6 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                        </div>
                        <h2 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                            {newPassword ? 'Пароль сгенерирован' : 'Сброс пароля'}
                        </h2>
                    </div>
                    <button onClick={onClose} className={darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}>
                        <X className="w-6 h-6" />
                    </button>
                </div>

                {!newPassword ? (
                    <>
                        <p className={`text-center mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            Сбросить пароль для пользователя:
                        </p>
                        <p className={`text-center font-bold text-lg mb-6 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                            {user.name} ({user.username})
                        </p>
                        <div className="flex justify-center space-x-4">
                            <button
                                onClick={onClose}
                                className={`px-6 py-2.5 rounded-lg transition-colors ${
                                    darkMode ? 'border border-gray-600 text-gray-300 hover:bg-gray-700' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}
                                disabled={isLoading}
                            >
                                Отмена
                            </button>
                            <button
                                onClick={handleReset}
                                disabled={isLoading}
                                className={`px-6 py-2.5 rounded-lg transition-colors flex items-center ${
                                    darkMode ? 'bg-blue-600 hover:bg-blue-700 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white'
                                } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                                {isLoading ? 'Генерация...' : 'Сгенерировать новый пароль'}
                                {!isLoading && <Key className="w-4 h-4 ml-2" />}
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="space-y-4">
                        <p className={`text-center ${darkMode ? 'text-green-300' : 'text-green-700'}`}>
                            Новый пароль успешно сгенерирован!
                        </p>
                        <div className={`p-5 rounded-lg border ${darkMode ? 'bg-blue-900/30 border-blue-800' : 'bg-blue-50 border-blue-200'}`}>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Новый пароль для пользователя:</p>
                            <p className="font-bold text-lg text-center text-blue-800 dark:text-blue-300 mb-1">{user.username}</p>
                            <div className="relative mt-2">
                                <input
                                    type="text"
                                    value={newPassword}
                                    readOnly
                                    className={`w-full px-4 py-3 text-lg font-mono text-center rounded-lg ${
                                        darkMode ? 'bg-gray-800 text-green-400' : 'bg-white text-green-700'
                                    }`}
                                />
                                <button
                                    onClick={copyToClipboard}
                                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 bg-blue-100 dark:bg-blue-800/50 rounded hover:bg-blue-200 dark:hover:bg-blue-700/50 transition-colors"
                                >
                                    <Copy className="w-4 h-4 text-blue-700 dark:text-blue-300" />
                                </button>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className={`w-full px-4 py-2.5 rounded-lg transition-colors ${
                                darkMode ? 'bg-green-700 hover:bg-green-600 text-white' : 'bg-green-600 hover:bg-green-700 text-white'
                            }`}
                        >
                            Готово, пароль передан пользователю
                        </button>
                    </div>
                )}
            </div>
        </motion.div>
    );
};